import { Badge, Box, Card, CircularProgress, FormHelperText, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import defaultImage from 'assets/images/placeholder-image.png';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { validateMainStrain } from 'validations/strain';
import { openSnackbar, uploadImage } from '../../redux/slices/common';
import { isEmptyObject } from 'utilities/common';
import { getMainStrain, updateMainStrain } from '../../redux/slices/strain';
import CommonQuillEditor from '../../common/editor';
import { useNavigate } from 'react-router-dom';
import colors from 'assets/theme/base/colors';
import StiloForm from './stiloForm';
import { STILO } from 'utilities/constants';
import { getData } from 'utilities/common';

export default function AddMainStrainForm() {
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const { mainStrainData, loading = false } = useSelector(state => state.strain);
  const { isUploading } = useSelector(state => state.common);
  const { grey } = colors;
  const adminsMode = getData('adminsMode')
  const isStilo = JSON.parse(adminsMode) === STILO

  const [strainData, setStrainData] = useState({
    image: '',
    metaTitle: '',
    metaDescription: '',
    title: '',
    description: '',
  });
  const navigate = useNavigate();

  const strainImageUploader = async (e) => {
    if (e.target.files.length) {
      const file = e.target.files[0];
      const body = new FormData();
      body.append('filePath', file);
      // res.fileUrl
      dispatch(uploadImage(body)).unwrap().then((res => {
        setStrainData({ ...strainData, image: res.fileUrl });
      }));
    }
  };
  
  const showError = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const addMainStrain = () => {
    const { errors: validationErrors, isValid: isDataValid } = validateMainStrain(strainData);
    if (!isDataValid) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    dispatch(updateMainStrain(strainData)).unwrap().then((res) => {
      showError(res?.message, 'success');
      navigate('/strains');
    });

  };
  useEffect(() => {
    if (!isEmptyObject(mainStrainData)) {
      setStrainData({ ...strainData, image: mainStrainData?.image, title: mainStrainData?.title, description: mainStrainData?.description, metaDescription: mainStrainData?.metaDescription, metaTitle: mainStrainData?.metaTitle });
    }
  }, [mainStrainData]);

  useEffect(() => {
    dispatch(getMainStrain());
  }, []);

  const uploadThumbnailImage = {
    borderRadius: '20px',
    width: '160px',
    height: '160px',
    objectFit: 'cover',
    boxShadow: '0 0 1px rgb(0 0 0 / 20%)'
  };
  const badgeCircularProgressIcon = {
    backgroundColor: '#fff',
    borderRadius: 10,
    p: .5,
    mr: 14,
    mt: 14
  };

  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }}>
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
        <Typography variant='h4' fontWeight="semibold" color="secondary" mb={4.5}>Edit Main Strain</Typography>
        {isStilo?
         <Grid container spacing={3.5}> 
        <StiloForm 
          errors={errors}
          strainData={strainData}
          setStrainData={setStrainData}
        />
        </Grid> 
        :
        <>
        <Typography variant='h5' fontWeight="semibold" mb={2}>{isStilo?"Banner Image":"Meta Image"}</Typography>
        
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25} mb={5}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
            badgeContent={
              isUploading ? <CircularProgress size={32} color='primary' sx={badgeCircularProgressIcon} /> : ''
            }
          >
            <img style={uploadThumbnailImage} src={strainData?.image || defaultImage} alt="" />
          </Badge>
          <label htmlFor="image-input">
            <MDButton
              sx={{ minWidth: '180px' }}
              type='button'
              variant='outlined'
              color='secondary'
              component='span'
              disabled={isUploading}
            >
              Change Image
            </MDButton>
          </label>
          <MDInput
            id="image-input"
            type="file"
            name="image"
            onChange={strainImageUploader}
            fullWidth
            disabled={isUploading}
            style={{ display: 'none' }}
          />
          {Boolean(errors?.image) && <FormHelperText sx={{ width: '100%', ml: 0, mt: -.5 }} error>{errors?.image}</FormHelperText>}
        </Stack>       
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Title</Typography>
            <div className="remove-quill-mb">
              <CommonQuillEditor
                value={strainData?.title || ''}
                onChange={(value) => setStrainData({ ...strainData, title: value })}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Meta Title</Typography>
            <div className="remove-quill-mb">
              <CommonQuillEditor
                value={strainData?.metaTitle || ''}
                onChange={(value) => setStrainData({ ...strainData, metaTitle: value })}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Meta Description</Typography>
            <div className="remove-quill-mb">
              <CommonQuillEditor
                value={strainData?.metaDescription || ''}
                onChange={(value) => setStrainData({ ...strainData, metaDescription: value })}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Description</Typography>
            <div className="remove-quill-mb">
              <CommonQuillEditor
                value={strainData?.description || ''}
                onChange={(value) => setStrainData({ ...strainData, description: value })}
              />
            </div>
          </Grid>
        </Grid>
        </>
        }

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={4} mb={2}>
          <MDButton
            sx={{ minWidth: '160px' }}
            type='submit'
            variant='contained'
            color='primary'
            onClick={addMainStrain}
            loading={loading}
          >
            {loading ? <CircularProgress size={22} style={{ color: 'white' }} /> : 'Update'}
          </MDButton>
          <MDButton
            sx={{ minWidth: '160px' }}
            type='button'
            variant='outlined'
            color='dark'
            onClick={() => navigate('/strains?orderFilter=main_strains')}
          >
            Cancel
          </MDButton>
        </Stack>
      </Card>
    </Box>
  );
}